import pica from 'pica';

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const MAX_DIMENSION = 2000;
const MIN_QUALITY = 0.4;
const QUALITY_STEP = 0.1;
const INITIAL_QUALITY = 0.9;

const picaInstance = pica();

export interface ProcessedImageResult {
  file: File;
  width: number;
  height: number;
  quality: number;
  finalSize: number;
  processed: boolean;
  reasons: string[];
}

interface ProcessImageOptions {
  // eslint-disable-next-line no-unused-vars
  onLog?(message: string): void;
}

const formatMegabytes = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(2)}MB`;

const canvasToJpegBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to generate JPEG blob'));
          return;
        }

        resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });

const getJpegFileName = (fileName: string): string => {
  const baseName = fileName.replace(/\.[^/.]+$/, '');
  return `${baseName || 'upload'}.jpg`;
};

export async function processImageForUpload(inputFile: File, options: ProcessImageOptions = {}): Promise<ProcessedImageResult> {
  const { onLog } = options;
  let bitmap: ImageBitmap | null = null;

  try {
    onLog?.('Processing image for upload...');
    bitmap = await window.createImageBitmap(inputFile);

    onLog?.(`Original image: ${formatMegabytes(inputFile.size)} ${bitmap.width}x${bitmap.height} ${inputFile.type || 'unknown'}`);

    const needsConversion = inputFile.type !== 'image/jpeg';
    const needsResize = bitmap.width > MAX_DIMENSION || bitmap.height > MAX_DIMENSION;
    const needsCompression = inputFile.size > MAX_FILE_SIZE;
    const reasons = [...(needsConversion ? ['JPEG normalization'] : []), ...(needsResize ? ['dimension resize'] : []), ...(needsCompression ? ['size compression'] : [])];

    if (!needsConversion && !needsResize && !needsCompression) {
      onLog?.('Image already matches upload thresholds. Skipping resize/compression.');

      return {
        file: inputFile,
        width: bitmap.width,
        height: bitmap.height,
        quality: 1,
        finalSize: inputFile.size,
        processed: false,
        reasons: [],
      };
    }

    onLog?.(`Processing required: ${reasons.join(', ')}`);

    let targetWidth = bitmap.width;
    let targetHeight = bitmap.height;

    if (needsResize) {
      const scale = Math.min(MAX_DIMENSION / bitmap.width, MAX_DIMENSION / bitmap.height);
      targetWidth = Math.round(bitmap.width * scale);
      targetHeight = Math.round(bitmap.height * scale);
    }

    onLog?.(`Resize start: ${bitmap.width}x${bitmap.height} -> ${targetWidth}x${targetHeight}`);

    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = bitmap.width;
    sourceCanvas.height = bitmap.height;

    const sourceCtx = sourceCanvas.getContext('2d');
    if (!sourceCtx) {
      throw new Error('Failed to create source canvas context');
    }

    sourceCtx.drawImage(bitmap, 0, 0);

    const destCanvas = document.createElement('canvas');
    destCanvas.width = targetWidth;
    destCanvas.height = targetHeight;

    await picaInstance.resize(sourceCanvas, destCanvas);
    onLog?.(`Resize success: ${targetWidth}x${targetHeight}`);

    let quality = INITIAL_QUALITY;
    let blob: Blob | null = null;

    while (quality >= MIN_QUALITY) {
      const normalizedQuality = Number(quality.toFixed(1));
      blob = await canvasToJpegBlob(destCanvas, normalizedQuality);
      onLog?.(`Compression quality=${normalizedQuality.toFixed(1)} size=${formatMegabytes(blob.size)}`);

      if (blob.size <= MAX_FILE_SIZE) {
        quality = normalizedQuality;
        break;
      }

      quality = Number((normalizedQuality - QUALITY_STEP).toFixed(1));
    }

    if (!blob || blob.size > MAX_FILE_SIZE) {
      const message = `Unable to compress image below 2MB. Final attempt: ${blob ? formatMegabytes(blob.size) : 'no blob'}`;
      throw new Error(message);
    }

    const outputFile = new File([blob], getJpegFileName(inputFile.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    onLog?.(`Resized image: ${formatMegabytes(blob.size)} ${targetWidth}x${targetHeight} image/jpeg quality=${quality.toFixed(1)}`);

    return {
      file: outputFile,
      width: targetWidth,
      height: targetHeight,
      quality,
      finalSize: blob.size,
      processed: true,
      reasons,
    };
  } catch (error) {
    onLog?.(`Compression failure: ${error instanceof Error ? error.message : String(error)}`);
    throw error;
  } finally {
    bitmap?.close();
  }
}
